import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ConfirmModal } from "@/components/modal/ConfirmModal";
import { RejectRequestModal } from "@/components/modal/RejectRequestModal";
import { RequestTypes } from "@/constants/enums";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { PurchaseOrderIntentDetailActions } from "@/components/common/section-components/requests/purchase-order-intent/PurchaseOrderIntentDetailActions";
import { PurchaseOrderIntentMainData } from "@/components/common/section-components/requests/purchase-order-intent/PurchaseOrderIntentMainData";
import { PurchaseOrderIntentMessagesTable } from "@/components/common/section-components/requests/purchase-order-intent/PurchaseOrderIntentMessagesTable";
import { PurchaseOrderIntentProductsTable } from "@/components/common/section-components/requests/purchase-order-intent/PurchaseOrderIntentProductsTable";
import { PurchaseOrderIntentStatus } from "@/constants/entity-status";
import { EstadosIntencionCompra } from "@/constants/enums/status/entity-status-ids";
import {
  fetchPurchaseOrderIntentDetailHandler,
  updatePurchaseOrderIntentStatusHandler,
} from "./schema/handler-functions";
import {
  PurchaseOrderIntentDetail,
  PurchaseOrderIntentProduct,
} from "./schema/purchase-order-intent-detail.schema";
import { NegotiationModal } from "@/components/modal/NegotiationModal";

const DetailLegend = ({ title }: { title: string }) => {
  return (
    <Flex alignItems="center" gap="30px">
      <Text fontWeight={700}>{title}</Text>
    </Flex>
  );
};

export const PurchaseOrderIntentDetailPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions, userPermissions } = useRoles();

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingPresent, setLoadingPresent] = useState(false);
  const [loadingSendInfo, setLoadingSendInfo] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);

  const [detail, setDetail] = useState<PurchaseOrderIntentDetail | null>(null);

  const { open: openPresentModal, setOpen: setOpenPresentModal } =
    useDisclosure();
  const { open: openInfoModal, setOpen: setOpenInfoModal } = useDisclosure();
  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const canWritePurchaseOrderIntent = useMemo(() => {
    return (userPermissions ?? []).some(
      (p) =>
        p?.codigo === PermissionCode.PURCHASE_ORDER_INTENT &&
        p?.tipoPermiso === "W",
    );
  }, [userPermissions]);

  const handleBack = () => {
    navigate(AppRoutes.PURCHASE_ORDER_INTENT);
  };

  const fetchDetail = async (showLoad = true) => {
    await fetchPurchaseOrderIntentDetailHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingDetail,
      setDetail,
      showLoad,
    });
  };

  useEffect(() => {
    if (id && isActive) {
      fetchDetail();
    }
  }, [id, isActive]);

  const productsTableItems =
    detail?.productos
      ?.map((p: PurchaseOrderIntentProduct) => ({
        idProducto: p.idProducto,
        nombreProducto: p.nombreProducto,
        unidadMedida: p.unidadMedida,
        cantidad: `${new Intl.NumberFormat("es-AR").format(p?.cantidad ?? 0)}`,
      })) ?? [];

  const loading =
    loadingDetail || loadingPresent || loadingSendInfo || loadingReject;

  const canPresent =
    detail?.estado === PurchaseOrderIntentStatus.PENDING ||
    detail?.estado === PurchaseOrderIntentStatus.IN_COMMUNICATION;

  const canSendInfo =
    detail?.estado === PurchaseOrderIntentStatus.PENDING ||
    detail?.estado === PurchaseOrderIntentStatus.IN_COMMUNICATION;

  const canReject =
    detail?.estado !== PurchaseOrderIntentStatus.REJECTED &&
    detail?.estado !== PurchaseOrderIntentStatus.CANCELLED &&
    detail?.estado !== PurchaseOrderIntentStatus.PRESENTED;

  const handlePresent = async () => {
    await updatePurchaseOrderIntentStatusHandler({
      id,
      navigate,
      fetchPermissions,
      estado: EstadosIntencionCompra.Presentada,
      setLoading: setLoadingPresent,
      setOpenModal: setOpenPresentModal,
      fetchDetail,
      successMessage: "Intención de compra presentada exitosamente.",
      errorMessage: "Hubo un error al presentar la intención de compra.",
    });
  };

  const handleSendInfo = async (event?: FieldValues) => {
    await updatePurchaseOrderIntentStatusHandler({
      id,
      navigate,
      fetchPermissions,
      estado: EstadosIntencionCompra.EnComunicacion,
      event,
      setLoading: setLoadingSendInfo,
      setOpenModal: setOpenInfoModal,
      fetchDetail,
      successMessage: "Comunicación iniciada con éxito.",
      errorMessage: "Hubo un error al marcar como en comunicación.",
    });
  };

  const handleReject = async (event?: FieldValues) => {
    await updatePurchaseOrderIntentStatusHandler({
      id,
      navigate,
      fetchPermissions,
      estado: EstadosIntencionCompra.Rechazada,
      event,
      setLoading: setLoadingReject,
      setOpenModal: setOpenRejectModal,
      fetchDetail,
      successMessage: "Intención de compra rechazada exitosamente.",
      errorMessage: "Hubo un error al rechazar la intención de compra.",
    });
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title={`Intención de compra N° ${id ?? "-"}`}
        onBack={handleBack}
        backButton
        loading={loading}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="16px"
        >
          <PurchaseOrderIntentProductsTable
            items={productsTableItems}
            rightElement={
              canWritePurchaseOrderIntent && (
                <PurchaseOrderIntentDetailActions
                  loadingPresent={loadingPresent}
                  loadingSendInfo={loadingSendInfo}
                  loadingReject={loadingReject}
                  setOpenInfoModal={setOpenInfoModal}
                  setOpenRejectModal={setOpenRejectModal}
                  onPresent={() => setOpenPresentModal(true)}
                  showPresent={canPresent}
                  showNeedsInfo={canSendInfo}
                  showReject={canReject}
                />
              )
            }
          />

          <PurchaseOrderIntentMainData
            titularCuenta={detail?.titularCuenta}
            cuenta={detail?.cuenta}
            telefono={detail?.telefono}
            zonaComercial={detail?.zonaComercial}
            tipoInsumo={detail?.tipoInsumo}
            estadoDescripcion={detail?.estado}
          />

          <DetailLegend title="Detalle" />

          <Flex flexDir="column" flexShrink={0}>
            <PurchaseOrderIntentMessagesTable
              messages={detail?.mensajes || []}
            />
          </Flex>
        </Flex>
      )}

      {detail?.id && (
        <>
          <ConfirmModal
            openConfirm={openPresentModal}
            setOpenConfirm={setOpenPresentModal}
            handleAccept={handlePresent}
            loadingAccept={loadingPresent}
            title="Presentar intención de compra"
            subtitle="¿Desea presentar esta intención de compra?"
          />

          <NegotiationModal
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            handleAccept={handleSendInfo}
            loadingAccept={loadingSendInfo}
            idTipoSolicitud={RequestTypes.IntencionCompra}
          />

          <RejectRequestModal
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleReject}
            loadingAccept={loadingReject}
            idTipoSolicitud={RequestTypes.IntencionCompra}
            entityName="intención de compra"
          />
        </>
      )}
    </Flex>
  );
};
